import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  ADD_SCRATCHPAD_NOTE,
  CREATE_SCRATCHPAD,
  EDIT_SCRATCHPAD,
  GET_SCRATCHPADS,
  GET_SCRATCHPAD,
  DELETE_SCRATCHPAD_NOTE,
  EDIT_SCRATCHPAD_TEXT_NOTE,
  DELETE_SCRATCHPAD,
  MOVE_SCRATCHPAD_NOTE,
  VISIT_SCRATCHPAD,
  SCRATCHPAD_EDIT_ENTITY_PERMISSIONS,
} from '../../utils/apollo/scratchpad/mutations';
import {
  IAddScratchpadNoteMutation,
  IAddScratchpadNoteMutationVariables,
  IGetScratchpadQuery,
  IGetScratchpadQueryVariables,
  IGetScratchpadsQuery,
  ICreateScratchpadMutation,
  IEditScratchpadMutation,
  ICreateScratchpadMutationVariables,
  IEditScratchpadMutationVariables,
  ScratchpadNoteType,
  IDeleteScratchpadNoteMutation,
  IDeleteScratchpadNoteMutationVariables,
  IEditScratchpadTextNoteMutation,
  IEditScratchpadTextNoteMutationVariables,
  IScratchpadTextNoteEditInput,
  IScratchpadNoteDeleteInput,
  IScratchpadNoteAddInput,
  IDeleteScratchpadMutation,
  IDeleteScratchpadMutationVariables,
  IMoveScratchpadNoteMutation,
  IMoveScratchpadNoteMutationVariables,
  IVisitScratchpadMutation,
  IVisitScratchpadMutationVariables,
  IScratchpadBasicFragmentFragment,
  IScratchpadEntityPermissionEditMutation,
  IScratchpadEntityPermissionEditMutationVariables,
  IEntityPermissionEditInput,
} from '../../utils/apollo/graphqlSchema';
import { isEmpty, isNil } from 'lodash';
import { assertNotNil } from '../../utils/types';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import { EMPTY_RICH_TEXT_CONTENT } from '../RichTextEditor';
import { isNotNil } from '../../utils/types';
import { IScratchPadWithNonNullableNotes } from './types';
import { clearAllScratchpadAnalysesAction } from '../../redux/slices/analyses';
import { useAppDispatch } from '../../redux/hooks';
import {
  scratchpadCreated,
  scratchpadDeleted,
  scratchpadVisited,
  scratchpadUpdated,
  scratchpadShared,
  scratchpadUserRefreshed,
} from '../../utils/analytics/events/scratchpad/events';
import { useAnalytics } from '../../utils/analytics/useAnalytics';

interface ScratchpadContextType {
  scratchpads: Array<IScratchpadBasicFragmentFragment>;
  selectedScratchpad: string | null;
  currentScratchpad: IScratchPadWithNonNullableNotes | null;
  loading: boolean;
  error?: ApolloError;
  setSelectedScratchpad: (id: string | null) => void;
  handleTitleChange: (newTitle: string) => Promise<void>;
  handleCreateNew: (name?: string) => Promise<string | undefined>;
  addNote: (input: IScratchpadNoteAddInput) => Promise<void>;
  deleteNote: (input: IScratchpadNoteDeleteInput) => Promise<void>;
  editNote: (input: IScratchpadTextNoteEditInput) => Promise<void>;
  deleteScratchpad: (id: string) => Promise<void>;
  moveNote: (noteId: string, seq: number) => Promise<void>;
  visitScratchpad: (id: string) => Promise<void>;
  editScratchpadPermissions: (
    input: IEntityPermissionEditInput,
  ) => Promise<boolean>;
  refreshScratchpad: () => Promise<void>;
}

const ScratchpadContext = createContext<ScratchpadContextType | undefined>(
  undefined,
);

export const ScratchpadProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const [selectedScratchpad, setSelectedScratchpad] = useState<string | null>(
    null,
  );

  const {
    data: scratchpadsData,
    refetch: refetchScratchpads,
    loading: scratchpadsLoading,
  } = useQuery<IGetScratchpadsQuery>(GET_SCRATCHPADS, {
    onCompleted: (data) => {
      if (data.scratchpads.entities.length > 0) {
        setSelectedScratchpad(data.scratchpads.entities[0].id);
      }
    },
  });

  const {
    data: currentScratchpadData,
    loading: currentScratchpadLoading,
    error,
    refetch: refetchCurrent,
  } = useQuery<IGetScratchpadQuery, IGetScratchpadQueryVariables>(
    GET_SCRATCHPAD,
    {
      variables: { id: selectedScratchpad! },
      skip: !selectedScratchpad || isEmpty(selectedScratchpad),
    },
  );

  const [createScratchpad] = useMutation<
    ICreateScratchpadMutation,
    ICreateScratchpadMutationVariables
  >(CREATE_SCRATCHPAD);

  const [editScratchpad] = useMutation<
    IEditScratchpadMutation,
    IEditScratchpadMutationVariables
  >(EDIT_SCRATCHPAD);

  const [addNoteMutation] = useMutation<
    IAddScratchpadNoteMutation,
    IAddScratchpadNoteMutationVariables
  >(ADD_SCRATCHPAD_NOTE);

  const [deleteNoteMutation] = useMutation<
    IDeleteScratchpadNoteMutation,
    IDeleteScratchpadNoteMutationVariables
  >(DELETE_SCRATCHPAD_NOTE);

  const [editScratchpadTextNote] = useMutation<
    IEditScratchpadTextNoteMutation,
    IEditScratchpadTextNoteMutationVariables
  >(EDIT_SCRATCHPAD_TEXT_NOTE);

  const [deleteScratchpadMutation] = useMutation<
    IDeleteScratchpadMutation,
    IDeleteScratchpadMutationVariables
  >(DELETE_SCRATCHPAD);

  const [moveNoteMutation] = useMutation<
    IMoveScratchpadNoteMutation,
    IMoveScratchpadNoteMutationVariables
  >(MOVE_SCRATCHPAD_NOTE);

  const [visitScratchpadMutation] = useMutation<
    IVisitScratchpadMutation,
    IVisitScratchpadMutationVariables
  >(VISIT_SCRATCHPAD);

  const [editEntityPermissions] = useMutation<
    IScratchpadEntityPermissionEditMutation,
    IScratchpadEntityPermissionEditMutationVariables
  >(SCRATCHPAD_EDIT_ENTITY_PERMISSIONS);

  const handleTitleChange = useCallback(
    async (newTitle: string) => {
      if (!selectedScratchpad) {
        return;
      }

      const result = await editScratchpad({
        variables: {
          input: {
            scratchpadId: selectedScratchpad,
            title: newTitle,
          },
        },
      });

      if (result.data?.scratchpadEdit.errors.length) {
        printGraphqlErrors(result.data.scratchpadEdit.errors);
        return;
      }

      await refetchScratchpads();

      const scratchpadToUpdate = currentScratchpadData?.scratchpadGet;
      if (scratchpadToUpdate) {
        track(scratchpadUpdated(scratchpadToUpdate));
      }
    },
    [
      selectedScratchpad,
      editScratchpad,
      refetchScratchpads,
      track,
      currentScratchpadData,
    ],
  );

  const addNote = useCallback(
    async (input: IScratchpadNoteAddInput) => {
      const result = await addNoteMutation({
        variables: { input },
      });

      if (result.data?.scratchpadNoteAdd.errors.length) {
        printGraphqlErrors(result.data.scratchpadNoteAdd.errors);
        return;
      }

      if (input.scratchpadId) {
        await refetchCurrent({
          id: input.scratchpadId,
        });

        const scratchpadToUpdate = currentScratchpadData?.scratchpadGet;
        if (scratchpadToUpdate) {
          track(scratchpadUpdated(scratchpadToUpdate));
        }
      }
    },
    [addNoteMutation, refetchCurrent, track, currentScratchpadData],
  );

  const deleteNote = useCallback(
    async (input: IScratchpadNoteDeleteInput) => {
      const { noteId, scratchpadId } = input;
      const notes = currentScratchpadData?.scratchpadGet?.notes;
      const noteIndex = notes?.findIndex((note) => note?.id === noteId);
      if (isNil(noteIndex)) {
        return;
      }

      const beforeNote = notes?.[noteIndex - 1];
      const afterNote = notes?.[noteIndex + 1];

      // Merge notes if they are both rich text notes
      if (
        beforeNote?.content?.__typename === 'RichTextNote' &&
        afterNote?.content?.__typename === 'RichTextNote'
      ) {
        const beforeNoteContent = beforeNote.content.richText;
        const afterNoteContent = afterNote.content.richText;

        const mergedContent = beforeNoteContent.html + afterNoteContent.html;

        // Edit the first note with merged content
        await editScratchpadTextNote({
          variables: {
            input: {
              scratchpadId,
              noteId: beforeNote.id,
              html: mergedContent,
            },
          },
        });

        // Delete the middle note (original deletion target)
        await deleteNoteMutation({
          variables: { input },
        });

        // Delete the third note (since it's now merged into the first)
        await deleteNoteMutation({
          variables: {
            input: {
              scratchpadId,
              noteId: afterNote.id,
            },
          },
        });

        await refetchCurrent();

        const scratchpadToUpdate = currentScratchpadData?.scratchpadGet;
        if (scratchpadToUpdate) {
          track(scratchpadUpdated(scratchpadToUpdate));
        }

        return;
      }

      // Regular deletion if not merging notes
      const result = await deleteNoteMutation({
        variables: { input },
      });

      if (result.data?.scratchpadNoteDelete.errors.length) {
        printGraphqlErrors(result.data.scratchpadNoteDelete.errors);
        return;
      }

      if (selectedScratchpad) {
        await refetchCurrent();

        const scratchpadToUpdate = currentScratchpadData?.scratchpadGet;
        if (scratchpadToUpdate) {
          track(scratchpadUpdated(scratchpadToUpdate));
        }
      }
    },
    [
      deleteNoteMutation,
      editScratchpadTextNote,
      selectedScratchpad,
      refetchCurrent,
      track,
      currentScratchpadData,
    ],
  );

  const editNote = useCallback(
    async (input: IScratchpadTextNoteEditInput) => {
      const result = await editScratchpadTextNote({
        variables: { input },
      });

      if (result.data?.scratchpadTextNoteEdit.errors.length) {
        printGraphqlErrors(result.data.scratchpadTextNoteEdit.errors);
        return;
      }

      if (selectedScratchpad) {
        await refetchCurrent();

        const scratchpadToUpdate = currentScratchpadData?.scratchpadGet;
        if (scratchpadToUpdate) {
          track(scratchpadUpdated(scratchpadToUpdate));
        }
      }
    },
    [
      editScratchpadTextNote,
      selectedScratchpad,
      refetchCurrent,
      track,
      currentScratchpadData,
    ],
  );

  const handleCreateNew = useCallback(
    async (name?: string) => {
      const result = await createScratchpad({
        variables: {
          input: {
            title: name ?? 'New Scratchpad',
          },
        },
      });

      if (result.data?.scratchpadCreate.errors.length) {
        printGraphqlErrors(result.data.scratchpadCreate.errors);
        return;
      }

      const newScratchpad = result.data?.scratchpadCreate?.entity;

      if (newScratchpad) {
        track(scratchpadCreated(newScratchpad));
        await addNote({
          scratchpadId: newScratchpad.id,
          noteType: ScratchpadNoteType.Text,
          richText: {
            html: EMPTY_RICH_TEXT_CONTENT,
          },
          imageEmbed: null,
          cohort: null,
          dashboard: null,
          report: null,
          seq: null,
        });
        setSelectedScratchpad(newScratchpad.id);
        await refetchScratchpads();
        return newScratchpad.id;
      }
    },
    [
      createScratchpad,
      addNote,
      setSelectedScratchpad,
      refetchScratchpads,
      track,
    ],
  );

  const deleteScratchpad = useCallback(
    async (id: string) => {
      const scratchpadToDelete = currentScratchpadData?.scratchpadGet;
      const result = await deleteScratchpadMutation({
        variables: { input: { scratchpadId: id } },
      });

      if (result.data?.scratchpadDelete.errors.length) {
        printGraphqlErrors(result.data.scratchpadDelete.errors);
        return;
      }

      if (scratchpadToDelete) {
        track(scratchpadDeleted(scratchpadToDelete));
      }

      if (selectedScratchpad === id) {
        setSelectedScratchpad(null);
      }
      await refetchScratchpads();
    },
    [
      deleteScratchpadMutation,
      selectedScratchpad,
      setSelectedScratchpad,
      refetchScratchpads,
      track,
      currentScratchpadData,
    ],
  );

  const moveNote = useCallback(
    async (noteId: string, seq: number) => {
      if (!selectedScratchpad) {
        return;
      }

      const result = await moveNoteMutation({
        variables: {
          input: {
            scratchpadId: selectedScratchpad,
            noteId,
            seq,
          },
        },
      });

      if (result.data?.scratchpadNoteMove.errors.length) {
        printGraphqlErrors(result.data.scratchpadNoteMove.errors);
        return;
      }

      await refetchCurrent();

      const scratchpadToUpdate = currentScratchpadData?.scratchpadGet;
      if (scratchpadToUpdate) {
        track(scratchpadUpdated(scratchpadToUpdate));
      }
    },
    [
      moveNoteMutation,
      selectedScratchpad,
      refetchCurrent,
      track,
      currentScratchpadData,
    ],
  );

  const visitScratchpad = useCallback(
    async (scratchpadId: string) => {
      setSelectedScratchpad(scratchpadId);
      const result = await visitScratchpadMutation({
        variables: {
          input: {
            scratchpadId,
          },
        },
      });

      if (result.data?.scratchpadVisit.errors.length) {
        printGraphqlErrors(result.data.scratchpadVisit.errors);
      }

      if (result.data?.scratchpadVisit?.entity) {
        track(scratchpadVisited(result.data.scratchpadVisit.entity));
      }
    },
    [visitScratchpadMutation, setSelectedScratchpad, track],
  );

  const currentScratchpad = useMemo(() => {
    if (!currentScratchpadData?.scratchpadGet) {
      return null;
    }

    const notes =
      currentScratchpadData.scratchpadGet.notes?.filter(isNotNil) ?? [];

    return {
      ...currentScratchpadData.scratchpadGet,
      notes,
    } satisfies IScratchPadWithNonNullableNotes;
  }, [currentScratchpadData]);

  const editScratchpadPermissions = useCallback(
    async (input: IEntityPermissionEditInput) => {
      const scratchpadToShare = currentScratchpadData?.scratchpadGet;

      const result = await editEntityPermissions({
        variables: {
          input,
        },
      });

      if (result.data?.entityPermissionEdit.errors.length) {
        printGraphqlErrors(result.data.entityPermissionEdit.errors);
        return false;
      }

      if (scratchpadToShare) {
        track(scratchpadShared(scratchpadToShare));
      }

      await refetchCurrent();
      return true;
    },
    [editEntityPermissions, refetchCurrent, track, currentScratchpadData],
  );

  const refreshScratchpad = useCallback(async () => {
    dispatch(clearAllScratchpadAnalysesAction());

    await refetchScratchpads();
    // when there is no scratchpads created
    // then if you refresh from outside throws an error
    if (!selectedScratchpad) {
      return;
    }

    const currentId = selectedScratchpad;
    setSelectedScratchpad(null);
    setSelectedScratchpad(currentId);
    await refetchCurrent();

    if (currentScratchpadData) {
      track(scratchpadUserRefreshed(currentScratchpadData.scratchpadGet));
    }
  }, [
    refetchCurrent,
    refetchScratchpads,
    dispatch,
    selectedScratchpad,
    currentScratchpadData,
    track,
  ]);

  const value = useMemo<ScratchpadContextType>(
    () => ({
      scratchpads: scratchpadsData?.scratchpads.entities ?? [],
      selectedScratchpad,
      currentScratchpad,
      loading: currentScratchpadLoading || scratchpadsLoading,
      scratchpadsLoading,
      error,
      setSelectedScratchpad,
      handleTitleChange,
      handleCreateNew,
      addNote,
      deleteNote,
      editNote,
      deleteScratchpad,
      moveNote,
      visitScratchpad,
      editScratchpadPermissions,
      refreshScratchpad,
    }),
    [
      scratchpadsData,
      selectedScratchpad,
      currentScratchpad,
      currentScratchpadLoading,
      scratchpadsLoading,
      error,
      handleTitleChange,
      handleCreateNew,
      addNote,
      deleteNote,
      editNote,
      deleteScratchpad,
      moveNote,
      visitScratchpad,
      editScratchpadPermissions,
      refreshScratchpad,
    ],
  );

  return (
    <ScratchpadContext.Provider value={value}>
      {children}
    </ScratchpadContext.Provider>
  );
};

export const useScratchpadContext = () => {
  const context = useContext(ScratchpadContext);
  assertNotNil(context);
  return context;
};
